import { useEffect, useState } from 'react';
import { OctoMark } from './OctoMark';
import { cn } from '@/shared/lib/cn';

/**
 * Marka logolu yükleme göstergesi.
 *
 * Masaüstündeki `CustomLoadingPanel` karşılığı, iki farkla: logo dönmüyor, nefes alır
 * gibi hafifçe büyüyüp küçülüyor — logonun kendisi dönerken tanınmaz hale geliyordu —
 * ve bekleme uzadıkça mesaj ilerler, böylece ekranın canlı olduğu görülür.
 *
 * Hareket azaltma tercihinde ölçek değişmez; yalnızca soluk bir nabız kalır.
 */
export interface OctoLoaderProps {
  /** Sırayla gösterilecek mesajlar; ilki hemen, sonrakiler beklerken gelir. */
  messages?: string[];
  /** Mesajlar arası süre (ms). */
  step?: number;
  /** Kaplama: alanı örter. `'opaque'` altını tamamen gizler, `true` yarı saydam perdedir. */
  overlay?: boolean | 'opaque';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = { sm: 'h-10 w-10', md: 'h-16 w-16', lg: 'h-24 w-24' };

export function OctoLoader({
  messages = [],
  step = 2500,
  overlay = false,
  size = 'md',
  className,
}: OctoLoaderProps) {
  const [index, setIndex] = useState(0);

  // Son mesajda durulur: listeyi başa sarmak "yeniden başladı" hissi veriyordu.
  useEffect(() => {
    if (messages.length <= 1) return;
    setIndex(0);
    const timer = setInterval(() => {
      setIndex((i) => (i + 1 < messages.length ? i + 1 : i));
    }, step);
    return () => clearInterval(timer);
  }, [messages, step]);

  const body = (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      <OctoMark className={cn(SIZES[size], 'octo-loader-mark')} />
      {messages.length ? (
        <p
          key={index}
          className="text-fg-muted octo-loader-text max-w-64 text-center text-sm"
          aria-live="polite"
        >
          {messages[Math.min(index, messages.length - 1)]}
        </p>
      ) : null}
    </div>
  );

  if (!overlay) return body;
  return (
    <div
      className={cn(
        'absolute inset-0 z-40 flex items-center justify-center',
        overlay === 'opaque' ? 'bg-bg' : 'bg-overlay/70 backdrop-blur-[1px]',
      )}
      role="status"
    >
      {body}
    </div>
  );
}
